import {useMemo} from 'react'

import {PULLS_PATH} from './routes'
import type {DashboardSectionConfig} from '@github-ui/shared-dashboard-lists/pulls/types/list-types'

import type {LabelProps} from '@primer/react'
import type {Icon} from '@primer/octicons-react'

function getPullsSearchHref(query: string) {
  const search = new URLSearchParams({q: query}).toString()
  return `${PULLS_PATH}?${search}`
}

export interface CategoryDisplay {
  label: string
  variant: LabelProps['variant']
  icon?: Icon
  iconColor?: string
  iconColorClass?: string
}

// The `queries` strings below are used for user-facing display of the query logic
// that the Rails backend uses to populate each inbox section. Keep them
// in sync with `packages/dashboard_surface/app/lib/dashboard_surface/queries.rb`
// in github/github so the search the user copies matches what the section actually shows.
export const inboxSections: DashboardSectionConfig[] = [
  {
    id: 'review-requested',
    name: 'Needs your review',
    maxItems: 10,
    href: getPullsSearchHref('review-requested'),
    emptyDescription: 'Pull requests that need your review will appear here.',
    description: 'Pull requests that are waiting for your review',
    queries: [{query: 'is:pr user-review-requested:@me state:open archived:false sort:updated-desc'}],
  },
  {
    id: 'team-review-requested',
    name: "Needs your teams' review",
    maxItems: 10,
    href: getPullsSearchHref('team-review-requested'),
    emptyDescription: "Pull requests that need your teams' review will appear here.",
    description: 'Pull requests that are waiting for reviews from your team',
    queries: [{query: 'is:pr team-review-requested-user:@me state:open archived:false sort:updated-desc'}],
  },
  {
    id: 'your-drafts',
    name: 'Your drafts',
    maxItems: 10,
    href: getPullsSearchHref('your-drafts'),
    emptyDescription: 'Pull requests that you have started but not yet submitted for review will appear here.',
    description: 'Your pull requests that you have started but not yet submitted for review',
    queries: [{query: 'is:pr author:@me is:draft state:open archived:false sort:updated-desc'}],
  },
  {
    id: 'waiting-for-review',
    name: 'Waiting for review or checks',
    maxItems: 10,
    href: getPullsSearchHref('waiting-for-review'),
    emptyDescription: 'Pull requests that are waiting for review or have running checks will appear here.',
    description: 'Your pull requests that are waiting for a review or have running checks',
    queries: [
      {
        query:
          'is:pr author:@me state:open -is:draft archived:false -review:approved -review:changes_requested -status:failure sort:updated-desc',
      },
    ],
  },
  {
    id: 'needs-action',
    name: 'Needs action',
    maxItems: 10,
    href: getPullsSearchHref('needs-action'),
    emptyDescription:
      'Pull requests with failing checks, merge conflicts, outdated branches, or required changes will appear here.',
    description: 'Your pull requests with failing checks, merge conflicts, outdated branches, or requested changes',
    queries: [
      {
        label: 'Changes requested',
        query: 'is:pr author:@me state:open review:changes_requested archived:false sort:updated-desc -is:draft',
      },
      {
        label: 'Failing checks',
        query: 'is:pr author:@me state:open status:failure archived:false sort:updated-desc -is:draft',
      },
    ],
  },
  {
    id: 'ready-to-merge',
    name: 'Ready to merge',
    maxItems: 10,
    href: getPullsSearchHref('ready-to-merge'),
    emptyDescription: 'Pull requests that are ready to merge will appear here.',
    description: 'Your pull requests that are ready to merge',
    queries: [
      {
        query:
          'is:pr author:@me state:open -is:draft review:approved -review:changes_requested -status:failure -is:queued archived:false sort:updated-desc',
      },
    ],
  },
  {
    id: 'merge-queue',
    name: 'Merging',
    maxItems: 10,
    href: getPullsSearchHref('merge-queue'),
    emptyDescription: 'Pull requests that are being merged will appear here.',
    description: 'Your pull requests that are currently merging or are in the merge queue',
    queries: [
      {
        query:
          'is:pr is:queued state:open (author:@me OR (author:@copilot assignee:@me)) archived:false sort:updated-desc',
      },
    ],
  },
]

/**
 * Returns the metadata-enriched list of inbox sections, preserving the canonical
 * order from `inboxSections`.
 */
export function getAvailableInboxSections(availableSectionIds: readonly string[]): DashboardSectionConfig[] {
  const availableSet = new Set(availableSectionIds)
  return inboxSections.filter(section => availableSet.has(section.id))
}

/**
 * Convenience hook used by the layout to compute the default order list from
 * the definition-derived available section ids.
 */
export function useDefaultInboxSectionOrder(availableSectionIds: readonly string[]) {
  return useMemo(() => getAvailableInboxSections(availableSectionIds).map(s => s.id), [availableSectionIds])
}
